import { CloseOutlined } from '@ant-design/icons';
import { Flex, Tooltip } from 'antd';
import React, { useContext } from 'react';
import { useRelayEnvironment } from 'react-relay';
import styled from 'styled-components';

import {
    COLOR_TAG_FILL,
    COLOR_TAG_FILL_ERROR,
    COLOR_TAG_TEXT,
    COLOR_TAG_TEXT_ERROR,
    COLOR_TAG_TEXT_ERROR_HOVER,
    COLOR_TAG_TEXT_HOVER,
} from '../const';
import { getObjectFromRelayStoreById } from '../functions';
import useDispatchSetterStore from '../hooks/useDispatchSetterStore';
import useDynamicSelector from '../hooks/useDynamicSelector';
import { SmxPropsTableContext } from '../providers/propsTableProvider';
import { getSetterTable, ITableState } from '../store/slices/tableSlice';
import { FilterTableParamType, StoreFilterType, StoreGroupFilterType, TableFilterType } from '../types';
import TableListFilter from './tableListFilter';

interface ITableFilter {
    filterItem: TableFilterType;
    paramFilter?: FilterTableParamType;
}

const FlexStyled = styled(Flex).withConfig({
    shouldForwardProp: (prop) => prop !== 'checkCondition',
})<{ checkCondition?: boolean }>`
    background-color: ${COLOR_TAG_FILL};
    padding: 5px 15px;
    color: ${({ checkCondition }) => (checkCondition ? COLOR_TAG_TEXT : COLOR_TAG_TEXT_ERROR_HOVER)};
    z-index: 1000;
    position: relative;
    cursor: pointer;

    &:hover {
        background-color: ${({ checkCondition }) => (checkCondition ? COLOR_TAG_FILL : COLOR_TAG_FILL_ERROR)};
        color: ${({ checkCondition }) => (checkCondition ? COLOR_TAG_TEXT_HOVER : COLOR_TAG_TEXT_ERROR)};
    }
`;

const TableFilter: React.FC<ITableFilter> = ({ filterItem, paramFilter }) => {
    const environment = useRelayEnvironment();

    const { nameQuery, prefixStore } = useContext(SmxPropsTableContext);
    const setterTable = useDispatchSetterStore<ITableState>(getSetterTable(nameQuery + prefixStore));
    const listGroupFilters = useDynamicSelector('table', 'listGroupFilters');

    const onClickDel = () => {
        const parentFilter = { ...listGroupFilters[filterItem.parentKey] };
        const newListFilters = {} as StoreFilterType;

        Object.keys(parentFilter.listFilters)
            .filter((key) => key !== filterItem.key)
            .forEach((key) => {
                newListFilters[key] = parentFilter.listFilters[key];
            });

        if (!Object.keys(newListFilters).length) {
            const newListGroupFilters = {} as StoreGroupFilterType;

            Object.keys(listGroupFilters)
                .filter((key) => key !== filterItem.parentKey)
                .forEach((key) => {
                    newListGroupFilters[key] = listGroupFilters[key];
                });

            setterTable({
                listGroupFilters: newListGroupFilters,
            });
        } else {
            setterTable({
                listGroupFilters: {
                    ...listGroupFilters,
                    [filterItem.parentKey]: {
                        ...parentFilter,
                        listFilters: newListFilters,
                    },
                } as StoreGroupFilterType,
            });
        }
    };
    const isConditionChosen = Boolean(filterItem.value !== '' && filterItem.value !== undefined);

    const getValueForFilter = (filter: TableFilterType) => {
        switch (filter.condition) {
            case 'from_list':
                const object = getObjectFromRelayStoreById(environment, `${filter.value}`) as any;
                if (object) {
                    const keyField =
                        object.__typename === 'ListItem' ? 'name' : filter.option.key.split('.').pop() || '';
                    return object[keyField];
                }
                return '';
            case 'is_unknown':
            case 'has_any_value':
                return '';
            default:
                return filter.value;
        }
    };

    return (
        <FlexStyled checkCondition={isConditionChosen} align={'center'}>
            <TableListFilter filterItem={filterItem} typeField={filterItem.option.typeField!} paramFilter={paramFilter}>
                <Flex gap={5}>
                    <span style={{ fontWeight: 'bolder' }}>{filterItem.option.label}</span>
                    {!isConditionChosen ? (
                        <span>(click to change)</span>
                    ) : (
                        <Flex gap={5}>
                            <span>
                                {filterItem.condition === 'from_list'
                                    ? ' - '
                                    : filterItem.condition.replaceAll('_', ' ')}
                            </span>
                            <span>{getValueForFilter(filterItem)}</span>
                        </Flex>
                    )}
                    <Tooltip
                        placement="top"
                        title={'Remove this filter'}
                        color={'white'}
                        overlayInnerStyle={{ color: 'black' }}
                    >
                        <CloseOutlined onClick={onClickDel} />
                    </Tooltip>
                </Flex>
            </TableListFilter>
        </FlexStyled>
    );
};

export default TableFilter;
